import type { Rarity, StackKind } from '../types';
import { itemTemplate } from './items';

export type RecipeGroup = 'weapons' | 'armor' | 'backpacks' | 'consumables' | 'special' | 'relic';

export type RecipeOutput =
  | { kind: 'item'; templateId: string; rarity: Rarity }
  | { kind: 'stack'; stack: StackKind; qty: number };

export interface RecipeDef {
  id: string;
  name: string;
  group: RecipeGroup;
  output: RecipeOutput;
  workshop: number;
  cost: Partial<Record<StackKind, number>>;
  minutes: number;
  xp: number;
  flavor?: string;
}

/** Recipes only survivors with a firearm (equipped or in Storage) see and can craft. */
export const FIREARM_RECIPES: readonly string[] = ['r_ammo', 'r_hollow_point'];

/**
 * The bench (30. 9. 2026, after the first pilot day): jobs at once by Workshop level (one, two from
 * level 3, three from level 5), and how many more may wait. Until then one job ran and two waited.
 */
export const CRAFT_RULES = {
  parallel: [
    [1, 1],
    [3, 2],
    [5, 3],
  ] as [number, number][],
  waiting: 2,
  /** Workshop 5: crafting this much faster (with the survivor's craft speed, at most 70% off). */
  fast: { level: 5, cut: 0.2 },
} as const;

/** Jobs that run at the same time at this Workshop level. */
export const craftParallel = (workshopLevel: number) => CRAFT_RULES.parallel.filter(([lv]) => workshopLevel >= lv).at(-1)?.[1] ?? 1;

/** Jobs on the bench and waiting together. */
export const craftQueueMax = (workshopLevel: number) => craftParallel(workshopLevel) + CRAFT_RULES.waiting;

/** The most jobs any Workshop takes (the request's `count` limit). */
export const CRAFT_QUEUE_LIMIT = craftQueueMax(99);

/**
 * When the next job can start: as soon as one of the `lanes` is free. `jobs` are the jobs already
 * planned (each on the lane that was free when it started), `now` the earliest start.
 */
export function craftSlotStart(jobs: { startAt: number; finishAt: number }[], lanes: number, now: number): number {
  const free: number[] = Array.from({ length: Math.max(1, lanes) }, () => -Infinity);
  for (const j of [...jobs].sort((a, b) => a.startAt - b.startAt || a.finishAt - b.finishAt)) {
    // The lane it ran on: the latest one already free when it started, else the one free first.
    let i = -1;
    free.forEach((f, k) => {
      if (f <= j.startAt && (i < 0 || f > free[i])) i = k;
    });
    if (i < 0) i = free.indexOf(Math.min(...free));
    free[i] = Math.max(free[i], j.finishAt);
  }
  return Math.max(now, Math.min(...free));
}

/** One craft request can queue the same recipe several times (26. 9. 2026): the cost of `n`. */
export function scaleCost(cost: Partial<Record<StackKind, number>>, n: number): Partial<Record<StackKind, number>> {
  const out: Partial<Record<StackKind, number>> = {};
  for (const [k, v] of Object.entries(cost) as [StackKind, number][]) if (v) out[k] = v * n;
  return out;
}

/** How many times a recipe can go on the bench now: the free queue slots and what Storage pays for. */
export function craftMaxCount(cost: Partial<Record<StackKind, number>>, have: Partial<Record<StackKind, number>>, freeSlots: number): number {
  let n = Math.max(0, freeSlots);
  for (const [k, v] of Object.entries(cost) as [StackKind, number][]) if (v > 0) n = Math.min(n, Math.floor((have[k] ?? 0) / v));
  return n;
}

export const RECIPE_GROUPS: { id: RecipeGroup; name: string }[] = [
  { id: 'weapons', name: 'Weapons' },
  { id: 'armor', name: 'Armor' },
  { id: 'backpacks', name: 'Backpacks' },
  { id: 'consumables', name: 'Consumables' },
  { id: 'relic', name: 'Relic forge' },
];

/** Krug 5 (K4.15): a gear recipe is called what it makes ("Pipe Wrench", not "Sharpened pipe wrench"). */
const gear = (id: string, group: RecipeGroup, templateId: string, rarity: Rarity, cost: RecipeDef['cost'], flavor?: string): RecipeDef => {
  const name = itemTemplate(templateId).name;
  const ws = rarity === 'common' ? 1 : rarity === 'uncommon' ? 2 : rarity === 'rare' ? 3 : 4;
  const minutes = rarity === 'common' ? 5 : rarity === 'uncommon' ? 15 : rarity === 'rare' ? 40 : 90;
  const xp = rarity === 'common' ? 10 : rarity === 'uncommon' ? 25 : rarity === 'rare' ? 60 : 140;
  return { id, name, group, output: { kind: 'item', templateId, rarity }, workshop: ws, cost, minutes, xp, flavor };
};

const stack = (id: string, name: string, stackId: StackKind, qty: number, workshop: number, minutes: number, cost: RecipeDef['cost'], xp: number, flavor?: string): RecipeDef => ({
  id, name, group: 'consumables', output: { kind: 'stack', stack: stackId, qty }, workshop, cost, minutes, xp, flavor,
});

export const RECIPES: RecipeDef[] = [
  // Weapons
  gear('r_pipe_wrench', 'weapons', 'pipe_wrench', 'common', { metal: 4, scrap: 4 }, 'Plumbing, repurposed.'),
  gear('r_nail_bat', 'weapons', 'nail_bat', 'uncommon', { wood: 6, metal: 4, scrap: 8, cloth: 2 }, 'Old sport, new rules.'),
  gear('r_machete', 'weapons', 'machete', 'uncommon', { metal: 10, scrap: 8, cloth: 2 }),
  gear('r_scrap_pistol', 'weapons', 'scrap_pistol', 'rare', { metal: 16, electronics: 6, chemicals: 4, scrap: 20 }, 'Copper wire and faith.'),
  gear('r_stalker_crossbow', 'weapons', 'stalker_crossbow', 'rare', { wood: 12, metal: 10, cloth: 6, scrap: 20 }),
  gear('r_fire_axe', 'weapons', 'fire_axe', 'epic', { metal: 40, wood: 20, scrap: 60, blueprint: 1 }),
  gear('r_service_carbine', 'weapons', 'r12_service_carbine', 'epic', { metal: 50, electronics: 20, chemicals: 12, scrap: 70, blueprint: 1 }),
  // Armor (recipe ids from before the set catalog are kept so queued crafts still finish)
  gear('r_bandana_goggles', 'armor', 'bandana_goggles', 'common', { cloth: 4, scrap: 3 }),
  gear('r_patched_hoodie', 'armor', 'patched_hoodie', 'common', { cloth: 8, scrap: 2 }),
  gear('r_fingerless_gloves', 'armor', 'fingerless_gloves', 'common', { cloth: 4, scrap: 2 }),
  gear('r_torn_jeans', 'armor', 'torn_jeans', 'common', { cloth: 6, scrap: 2 }, 'More hole than denim.'),
  gear('r_work_boots', 'armor', 'steel_toe_boots', 'common', { cloth: 3, metal: 3, scrap: 3 }),
  gear('r_hard_hat', 'armor', 'hard_hat', 'uncommon', { metal: 8, cloth: 2, scrap: 8 }),
  gear('r_work_gloves', 'armor', 'work_gloves', 'uncommon', { cloth: 6, scrap: 6 }),
  gear('r_cargo_work_pants', 'armor', 'cargo_work_pants', 'uncommon', { cloth: 10, scrap: 6 }),
  gear('r_road_sign_vest', 'armor', 'road_sign_vest', 'uncommon', { metal: 10, cloth: 6, scrap: 10 }, 'Yield. Or don’t.'),
  gear('r_hiking_boots', 'armor', 'tread_boots', 'uncommon', { cloth: 8, metal: 2, scrap: 8 }),
  gear('r_moto_helmet', 'armor', 'bucket_helm', 'rare', { metal: 14, chemicals: 4, cloth: 4, scrap: 18 }),
  gear('r_tire_plate_pants', 'armor', 'tire_plate_pants', 'rare', { cloth: 12, metal: 8, scrap: 16 }),
  gear('r_leather_rider_jacket', 'armor', 'paramedic_jacket', 'rare', { cloth: 18, medicine: 2, metal: 4, scrap: 18 }),
  gear('r_firefighter_boots', 'armor', 'paramedic_boots', 'rare', { cloth: 10, metal: 8, chemicals: 4, scrap: 16 }),
  gear('r_insulated_gloves', 'armor', 'nitrile_gloves', 'rare', { cloth: 6, chemicals: 6, medicine: 2, scrap: 14 }),
  gear('r_scrub_pants', 'armor', 'scrub_pants', 'rare', { cloth: 14, medicine: 2, scrap: 14 }),
  gear('r_gas_mask_hood', 'armor', 'headlamp_cap', 'epic', { cloth: 20, electronics: 10, chemicals: 10, scrap: 50, blueprint: 1 }),
  gear('r_kevlar_vest', 'armor', 'stalker_coat', 'epic', { cloth: 30, metal: 20, chemicals: 12, scrap: 60, blueprint: 1 }),
  gear('r_combat_pants', 'armor', 'combat_pants', 'epic', { cloth: 26, metal: 16, scrap: 54, blueprint: 1 }),
  gear('r_tactical_boots', 'armor', 'soft_sole_boots', 'epic', { cloth: 20, metal: 16, scrap: 50, blueprint: 1 }),
  gear('r_tactical_gloves', 'armor', 'tactical_gloves', 'epic', { cloth: 14, metal: 14, scrap: 44, blueprint: 1 }),
  // Backpacks
  gear('r_duffel_sling', 'backpacks', 'duffel_sling', 'common', { cloth: 6, scrap: 2 }, 'Holds together. Mostly.'),
  gear('r_medic_satchel', 'backpacks', 'medic_satchel', 'uncommon', { cloth: 10, medicine: 2, scrap: 6 }),
  gear('r_hiking_pack', 'backpacks', 'hiking_pack', 'rare', { cloth: 18, metal: 8, fuel: 4, scrap: 14 }, 'Room for everything you shouldn’t carry.'),
  gear('r_tactical_pack', 'backpacks', 'tactical_pack', 'epic', { cloth: 34, metal: 16, scrap: 50, blueprint: 1 }),
  // Consumables
  stack('r_bandage', 'Bandage ×3', 'bandage', 3, 1, 6, { cloth: 3 }, 4, 'Clean-ish.'),
  stack('r_ration', 'Ration ×2', 'ration', 2, 1, 4, { food: 9 }, 3, 'Salt, smoke and patience.'),
  // Krug 5 (K12.3): the other meals; the same time and XP as the Ration. K12.10, round 2: Food +50% in every meal (Ration 6 → 9).
  stack('r_dried_jerky', 'Dried Jerky ×2', 'dried_jerky', 2, 1, 4, { food: 9, chemicals: 1 }, 3, 'Tough as a boot. Keeps like one too.'),
  stack('r_hot_stew', 'Hot Stew ×2', 'hot_stew', 2, 2, 4, { food: 12, wood: 2 }, 3, 'Whatever was in the pot. It works.'),
  stack('r_cold_beans', 'Cold Beans ×2', 'cold_beans', 2, 2, 4, { food: 9, metal: 1 }, 3, 'No fire, no smoke, no company.'),
  stack('r_canned_feast', 'Canned Feast', 'canned_feast', 1, 3, 4, { food: 15, metal: 1, fuel: 1 }, 3, 'A proper meal. Remember those?'),
  // Package 4, part O (owner, 7. 10. 2026): the new meals, Food and one other material (the Food grows with the
  // survivor's tier, MEAL_TIER); the same time and XP as the others.
  stack('r_station9_chili', 'Station 9 Chili ×2', 'station9_chili', 2, 2, 4, { food: 12, fuel: 1 }, 3, "Danny swears it's Tobias's recipe. Tobias swears it isn't."),
  stack('r_black_coffee', 'Black Coffee ×2', 'black_coffee', 2, 1, 4, { food: 9, wood: 2 }, 3, 'Boiled twice. Tastes like it.'),
  stack('r_halcyon_bar', 'Halcyon Protein Bar ×2', 'halcyon_bar', 2, 3, 4, { food: 12, chemicals: 1 }, 3, 'Be better, faster. The wrapper still says so.'),
  stack('r_canned_peaches', 'Canned Peaches ×2', 'canned_peaches', 2, 2, 4, { food: 9, metal: 1 }, 3, 'Worth arguing over.'),
  stack('r_fever_broth', 'Fever Broth ×2', 'fever_broth', 2, 2, 4, { food: 12, medicine: 1 }, 3, "Doc Mara's recipe. Drink it hot."),
  stack('r_night_stew', 'Night Stew ×2', 'night_stew', 2, 2, 4, { food: 15, wood: 2 }, 3, 'Left on the coals for a whole night shift.'),
  stack('r_horde_ration', 'Horde Ration ×2', 'horde_ration', 2, 3, 4, { food: 15, metal: 2 }, 3, 'Eaten standing up, at the wall.'),
  // Krug 5 (K1): only shown to survivors with a firearm (FIREARM_RECIPES).
  // 7. 10. 2026 (owner, Ammo per shot): a batch of 20 for four times the materials (a fight takes 4–6 rounds now; was ×5 for 2 Scrap and 1 Metal).
  stack('r_ammo', 'Ammo ×20', 'ammo', 20, 1, 10, { scrap: 8, metal: 4 }, 4, 'Old casings, new powder.'),
  // 7. 10. 2026 (owner, Ammo and Fuel): Hollow-point Rounds (a firearm's +10% for a run) and the Molotov (Fuel for every build).
  stack('r_hollow_point', 'Hollow-point Rounds', 'hollow_point', 1, 2, 10, { ammo: 10, chemicals: 2 }, 6, 'Softer tips. Harder landings.'),
  stack('r_molotov', 'Molotov', 'molotov', 1, 1, 6, { fuel: 2, cloth: 1 }, 4, 'A bottle, a rag, a bad day for somebody.'),
  stack('r_repair_kit', 'Repair kit', 'repair_kit', 1, 2, 12, { metal: 4, scrap: 4, cloth: 1 }, 8, 'Tape, wire, a spare screw you will lose.'),
  // 7. 10. 2026 (owner): 30 durability on any item, made for Epic and Legendary gear; a use for Electronics and Chemicals.
  stack('r_precision_repair_kit', 'Precision Repair Kit', 'precision_repair_kit', 1, 3, 20, { electronics: 5, chemicals: 4, metal: 4 }, 10, 'Calibrated by eye. Close enough.'),
  stack('r_painkillers', 'Painkillers', 'painkillers', 1, 2, 12, { medicine: 2, chemicals: 1 }, 8),
  stack('r_medkit', 'Medkit', 'medkit', 1, 3, 30, { medicine: 4, cloth: 3, chemicals: 1 }, 20),
  stack('r_stim', 'Stim', 'stim', 1, 3, 30, { chemicals: 3, medicine: 2 }, 20),
];

export const RECIPE_BY_ID: Record<string, RecipeDef> = Object.fromEntries(RECIPES.map((r) => [r.id, r]));
